/**
 * resultDisplay — render-time prettifier for stored step results.
 *
 * Logs written BEFORE the backend learned to render structured summaries
 * (and today's proof articles) contain raw tool-result JSON like
 * `[{"type":"text","text":"{\"count\": 3, \"alive_count\": 1, ...`} — often
 * TRUNCATED at 300 chars so it will not parse. This module mirrors the
 * backend's per-tool renderings (step_log.py) at render time:
 *
 * - verify_sources_alive  → "3 sources checked: 1 alive, 2 dead · 2 validated"
 * - verify_source_alive   → "energy.gov: alive (HTTP 200)" / "…: dead (…)"
 * - lookup_section_sources → "5 approved sources: bls.gov, energy.gov …"
 * - lookup_internal_link_candidates → "4 link candidates: <first title> …"
 * - fetch_source_text / get_source_text → "Fetched ~842 words from energy.gov"
 *
 * Already-human strings (FAILED n/m, PASSED n/n, plain sentences) pass
 * through untouched. Unparseable/unknown JSON falls back to
 * "Result recorded (raw)" — the table cell must never show braces; the raw
 * string stays available in the expanded detail row.
 */

const GENERIC_FALLBACK = 'Result recorded (raw)';

function bareToolName(tool?: string | null): string {
  if (!tool) return '';
  return (tool.split('__').pop() ?? tool).toLowerCase();
}

/** "https://www.energy.gov/x" → "energy.gov"; non-URLs → '' */
function domainOf(url: unknown): string {
  if (typeof url !== 'string' || !url.trim()) return '';
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(url.trim())
    ? url.trim()
    : `https://${url.trim()}`;
  try {
    const host = new URL(withScheme).hostname.replace(/^www\./, '');
    if (host && host.includes('.')) return host;
  } catch {
    // not a URL
  }
  return '';
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

// ---------------------------------------------------------------------------
// Per-tool renderers over a PARSED payload (mirror backend step_log.py)
// ---------------------------------------------------------------------------

type Payload = Record<string, unknown>;

function isInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function renderLivenessBatch(payload: Payload): string | null {
  const count = payload.count;
  const alive = payload.alive_count;
  const dead = payload.dead_count;
  if (!isInt(count) || !isInt(alive) || !isInt(dead)) return null;
  let summary = `${plural(count, 'source')} checked: ${alive} alive, ${dead} dead`;
  const validated = payload.validated_count;
  if (isInt(validated) && validated > 0) summary += ` · ${validated} validated`;
  return summary;
}

function renderLivenessSingle(payload: Payload): string | null {
  const isAlive = payload.is_alive;
  if (typeof isAlive !== 'boolean') return null;
  const domain =
    domainOf(payload.url) || domainOf(payload.normalized_url) || String(payload.url ?? 'source');
  let detail = '';
  const error = payload.validation_error;
  if (!isAlive && typeof error === 'string' && error.trim()) {
    detail = ` (${error.trim()})`;
  } else if (isInt(payload.status_code)) {
    detail = ` (HTTP ${payload.status_code})`;
  }
  return `${domain}: ${isAlive ? 'alive' : 'dead'}${detail}`;
}

function renderPoolSources(payload: Payload): string | null {
  const count = payload.count;
  const sources = payload.sources;
  if (!isInt(count) || !Array.isArray(sources)) return null;
  if (count === 0) return 'No approved sources found';
  const domains: string[] = [];
  for (const source of sources) {
    if (typeof source !== 'object' || source === null) continue;
    const record = source as Payload;
    const domain =
      (typeof record.domain === 'string' && record.domain.trim()) || domainOf(record.url);
    if (domain && !domains.includes(domain)) domains.push(domain);
    if (domains.length >= 2) break;
  }
  let summary = plural(count, 'approved source');
  if (domains.length > 0) {
    summary += `: ${domains.join(', ')}`;
    if (count > domains.length) summary += ' …';
  }
  return summary;
}

function renderInternalLinks(payload: Payload): string | null {
  const count = payload.count;
  const candidates = payload.candidates;
  if (!isInt(count) || !Array.isArray(candidates)) return null;
  if (count === 0) return 'No internal link candidates found';
  let firstTitle = '';
  for (const candidate of candidates) {
    if (typeof candidate === 'object' && candidate !== null) {
      const title = (candidate as Payload).title;
      if (typeof title === 'string' && title.trim()) {
        firstTitle = title.trim();
        break;
      }
    }
  }
  let summary = plural(count, 'link candidate');
  if (firstTitle) {
    summary += `: ${firstTitle}`;
    if (count > 1) summary += ' …';
  }
  return summary;
}

function renderFetchedText(payload: Payload): string | null {
  const hasTextField = 'text' in payload || 'full_text' in payload;
  const text = 'text' in payload ? payload.text : payload.full_text;
  const domain = domainOf(payload.url);
  if (typeof text === 'string' && text.trim()) {
    const words = text.trim().split(/\s+/).length;
    const base = `Fetched ~${plural(words, 'word')}`;
    return domain ? `${base} from ${domain}` : base;
  }
  if (!hasTextField) return null;
  const base = domain ? `No text fetched from ${domain}` : 'No text fetched';
  const status = payload.text_status;
  if (typeof status === 'string' && status.trim()) return `${base} (${status.trim()})`;
  return base;
}

const PAYLOAD_RENDERERS: Record<string, (payload: Payload) => string | null> = {
  verify_sources_alive: renderLivenessBatch,
  verify_source_alive: renderLivenessSingle,
  lookup_section_sources: renderPoolSources,
  lookup_internal_link_candidates: renderInternalLinks,
  fetch_source_text: renderFetchedText,
  get_source_text: renderFetchedText,
};

// ---------------------------------------------------------------------------
// Truncated-JSON regex extraction (repair-free)
//
// The stored summary is capped at 300 chars, so the JSON usually does not
// parse. Fields inside the wrapper are JSON-escaped (`\"count\": 3`), so the
// patterns tolerate optional backslashes before quotes.
// ---------------------------------------------------------------------------

function rxInt(raw: string, field: string): number | null {
  const match = raw.match(new RegExp(`\\\\?"${field}\\\\?"\\s*:\\s*(\\d+)`));
  return match ? parseInt(match[1], 10) : null;
}

function rxBool(raw: string, field: string): boolean | null {
  const match = raw.match(new RegExp(`\\\\?"${field}\\\\?"\\s*:\\s*(true|false)`));
  return match ? match[1] === 'true' : null;
}

function rxStr(raw: string, field: string): string | null {
  // Capture up to the next quote/backslash — good enough for urls, domains,
  // titles, and status tokens (they rarely contain escapes).
  const match = raw.match(new RegExp(`\\\\?"${field}\\\\?"\\s*:\\s*\\\\?"([^"\\\\]+)`));
  return match ? match[1] : null;
}

function rxStrAll(raw: string, field: string, max: number): string[] {
  const out: string[] = [];
  const regex = new RegExp(`\\\\?"${field}\\\\?"\\s*:\\s*\\\\?"([^"\\\\]+)`, 'g');
  let match: RegExpExecArray | null;
  while ((match = regex.exec(raw)) !== null && out.length < max) {
    if (!out.includes(match[1])) out.push(match[1]);
  }
  return out;
}

function rxIsNull(raw: string, field: string): boolean {
  return new RegExp(`\\\\?"${field}\\\\?"\\s*:\\s*null`).test(raw);
}

function extractByRegex(bare: string, raw: string): string | null {
  if (bare === 'verify_sources_alive') {
    const count = rxInt(raw, 'count');
    const alive = rxInt(raw, 'alive_count');
    const dead = rxInt(raw, 'dead_count');
    if (count == null || alive == null || dead == null) return null;
    let summary = `${plural(count, 'source')} checked: ${alive} alive, ${dead} dead`;
    const validated = rxInt(raw, 'validated_count');
    if (validated != null && validated > 0) summary += ` · ${validated} validated`;
    return summary;
  }
  if (bare === 'verify_source_alive') {
    const isAlive = rxBool(raw, 'is_alive');
    if (isAlive == null) return null;
    const domain = domainOf(rxStr(raw, 'url')) || domainOf(rxStr(raw, 'normalized_url'));
    const status = rxInt(raw, 'status_code');
    const detail = status != null ? ` (HTTP ${status})` : '';
    return `${domain || 'source'}: ${isAlive ? 'alive' : 'dead'}${detail}`;
  }
  if (bare === 'lookup_section_sources') {
    const count = rxInt(raw, 'count');
    if (count == null) return null;
    if (count === 0) return 'No approved sources found';
    const domains = rxStrAll(raw, 'domain', 2);
    let summary = plural(count, 'approved source');
    if (domains.length > 0) {
      summary += `: ${domains.join(', ')}`;
      if (count > domains.length) summary += ' …';
    }
    return summary;
  }
  if (bare === 'lookup_internal_link_candidates') {
    const count = rxInt(raw, 'count');
    if (count == null) return null;
    if (count === 0) return 'No internal link candidates found';
    const title = rxStr(raw, 'title');
    let summary = plural(count, 'link candidate');
    if (title) {
      summary += `: ${title}`;
      if (count > 1) summary += ' …';
    }
    return summary;
  }
  if (bare === 'fetch_source_text' || bare === 'get_source_text') {
    const domain = domainOf(rxStr(raw, 'url'));
    if (rxIsNull(raw, 'text') || rxIsNull(raw, 'full_text')) {
      const base = domain ? `No text fetched from ${domain}` : 'No text fetched';
      const status = rxStr(raw, 'text_status');
      return status ? `${base} (${status})` : base;
    }
    // Text present but truncated — a word count would lie, so don't fake one
    if (/\\?"(text|full_text)\\?"\s*:\s*\\?"/.test(raw)) {
      return domain ? `Fetched text from ${domain}` : 'Fetched text';
    }
    return null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** True when the stored summary is the raw `[{"type":"text"…` wrapper. */
function isTextBlockWrapper(raw: string): boolean {
  return /^\[\s*\{\s*\\?"type\\?"\s*:\s*\\?"text\\?"/.test(raw);
}

/** Parse the wrapper (when complete) and return the inner text payload. */
function extractInnerText(raw: string): string | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const texts = parsed
        .filter(
          (block): block is { type: string; text: string } =>
            typeof block === 'object' &&
            block !== null &&
            (block as Payload).type === 'text' &&
            typeof (block as Payload).text === 'string',
        )
        .map((block) => block.text);
      if (texts.length > 0) return texts.join('\n');
    }
  } catch {
    // truncated / not JSON
  }
  return null;
}

function renderParsedPayload(bare: string, text: string): string | null {
  const renderer = PAYLOAD_RENDERERS[bare];
  if (!renderer) return null;
  try {
    const payload: unknown = JSON.parse(text);
    if (typeof payload === 'object' && payload !== null && !Array.isArray(payload)) {
      return renderer(payload as Payload);
    }
  } catch {
    // truncated / not JSON
  }
  return null;
}

/**
 * Prettify a stored output_summary for display. Human text passes through;
 * JSON-ish strings become the per-tool rendering, a regex-extracted summary
 * for truncated JSON, or a generic fallback — never raw braces.
 */
export function humanizeResultDisplay(
  outputSummary: string,
  tool?: string | null,
): string {
  const raw = (outputSummary ?? '').trim();
  if (!raw) return raw;

  // Already-human strings (FAILED n/m, plain sentences) pass through.
  if (!raw.startsWith('[') && !raw.startsWith('{')) return outputSummary;

  const bare = bareToolName(tool);

  if (isTextBlockWrapper(raw)) {
    // Complete wrapper → parse inner JSON and render per tool
    const inner = extractInnerText(raw);
    if (inner != null) {
      const rendered = renderParsedPayload(bare, inner);
      if (rendered) return rendered;
      // Inner is plain human text (tool returned prose) → show it
      const innerTrimmed = inner.trim();
      if (innerTrimmed && !innerTrimmed.startsWith('{') && !innerTrimmed.startsWith('[')) {
        return innerTrimmed;
      }
    }
    // Truncated wrapper → repair-free regex extraction of known fields
    const extracted = extractByRegex(bare, raw);
    if (extracted) return extracted;
    return GENERIC_FALLBACK;
  }

  // Bare JSON object/array (not the wrapper): try parse-render, then regex
  const rendered = renderParsedPayload(bare, raw);
  if (rendered) return rendered;
  const extracted = extractByRegex(bare, raw);
  if (extracted) return extracted;
  return GENERIC_FALLBACK;
}
